import React from 'react';
import { connect } from 'react-redux';
import { Icon, Label } from 'semantic-ui-react';
import Parser from 'html-react-parser';
import { ConsultantTutorialStep } from '../../shared/api/api';

import { StateType } from '../../reducers';
import { consultantTutorialSelector } from '../../reducers/ConsultantTutorialReducer/ConsultantTutorialSelectors';

import { ConsultantAlphaTutorialResults } from './ConsultantAlphaTutorialResults';
import { useAlphaTutorialChecks } from '../../shared/api/apiHooks';
import { ErrorBoundary } from '../../containers';
import { useToggle } from 'react-use';
import { FormattedMessageHelper } from '../../shared/helpers/internationalization';
import './ConsultantAlphaTutorialNotification.scss';

export interface AlphaTutorialNotificationProps {
  alphaPath: string[];
}

interface AlphaTutorialNotificationStateProps {
  isTutorialModeActive: boolean;
  alphaId: string;
  alphaIsTutorialModeActive: boolean;
  alphaTutorialStep: number | undefined;
  alphaStatus: string;
  currentlyViewedStep: number;
  totalSteps: number;
  tutorialSteps: ConsultantTutorialStep[];
}

type AlphaTutorialNotificationGenericProps = AlphaTutorialNotificationProps & AlphaTutorialNotificationStateProps;

function ConsultantAlphaTutorialNotificationImpl({
  isTutorialModeActive,
  alphaId,
  alphaIsTutorialModeActive,
  alphaTutorialStep,
  alphaStatus,
  currentlyViewedStep,
  tutorialSteps,
}: AlphaTutorialNotificationGenericProps) {
  const isSubmitted = alphaStatus === 'ACTIVE';

  const [isOpened, toggleIsOpened] = useToggle(true);

  const isNotificationShown =
    isOpened &&
    isTutorialModeActive &&
    alphaIsTutorialModeActive &&
    typeof alphaTutorialStep === 'number' &&
    (currentlyViewedStep === alphaTutorialStep || currentlyViewedStep + 1 === alphaTutorialStep);

  const { isFetching, data: checkResponse } = useAlphaTutorialChecks({
    alphaId: alphaId,
    isSubmitted: isSubmitted,
    isDisabled: !isNotificationShown,
  });

  const currentStepIndex = currentlyViewedStep;

  const currentStep = tutorialSteps[currentStepIndex];
  const currentTutorialName = currentStep?.name ?? 'loading...';
  const currentStepName = currentStep?.task ?? 'loading...';
  const nextTutorialName = tutorialSteps[currentStepIndex + 1]?.name ?? 'loading...';

  if (!isNotificationShown) {
    return null;
  }

  return (
    <ErrorBoundary isNotFullScreen={true}>
      <div className="tutorial-notification alphas-details-content__box">
        <div className="tutorial-notification__top-section">
          <Label className="mode-label" color="green" size="tiny">
            <FormattedMessageHelper id="tutorialNotification.modeLabel" />
          </Label>
          <button
            className="tutorial-notification__header-close"
            onClick={toggleIsOpened}
            data-testid="tutorial-close-button"
            aria-label="Close tutorial notification"
          >
            <span className="tutorial-notification__header-close-icon" />
          </button>
        </div>

        <div className="tutorial-notification__explain-info">
          <Icon name="info circle" color="blue" />
          <FormattedMessageHelper id="tutorialNotification.infoExplanation" />
        </div>

        <h3 className="tutorial-notification__tutorial-name">
          <Icon name="book" color="green" />
          <FormattedMessageHelper id="tutorialNotification.header" values={{ tutorialName: currentTutorialName }} />
        </h3>

        <p>{Parser(currentStepName)}</p>

        <ConsultantAlphaTutorialResults
          nextTutorialName={nextTutorialName}
          stepIndex={alphaTutorialStep as number}
          isFetching={isFetching}
          checkResponse={checkResponse}
        />
      </div>
    </ErrorBoundary>
  );
}

const mapStateToProps = (state: StateType, ownProps: AlphaTutorialNotificationProps): AlphaTutorialNotificationStateProps => {
  const tutorialState = consultantTutorialSelector(state);

  return {
    isTutorialModeActive: tutorialState.isTutorialModeActive,
    alphaId: state.getIn(ownProps.alphaPath.concat(['alphaDetail', 'id'])) as string,
    alphaIsTutorialModeActive: state.getIn(ownProps.alphaPath.concat(['isTutorialModeActive'])) as boolean,
    alphaTutorialStep: state.getIn(ownProps.alphaPath.concat(['tutorialStep'])) as number | undefined,
    alphaStatus: state.getIn(ownProps.alphaPath.concat(['alphaDetail', 'status'])) as string,

    totalSteps: tutorialState.steps?.length ?? 1,
    currentlyViewedStep: tutorialState.currentlyViewedStep ?? 0,
    tutorialSteps: tutorialState.steps ?? [],
  };
};

export const ConsultantAlphaTutorialNotification = connect(mapStateToProps)(ConsultantAlphaTutorialNotificationImpl);
